import retainExtraPassesConstants from "@/constants/retainExtraPassesConstants"
import { apiV7nocache } from "@/store/modules/axiousWithCache.js"
import moment from "moment-timezone"

const getDefaultState = () => {
  return {
    retainedAppointments: new Map(),
    search_query: "",
    column_search_query: "",
    sort: null,
    betweenDates: [
      moment().format("MM/DD/YYYY").toString(),
      moment().format("MM/DD/YYYY").toString()
    ],
    retain_type: null,
    grade_years: null,
    areRetainedAppointmentsLoading: false,
    pagination: {
      activePage: 1,
      total: 0,
      pages: 0,
      per_page: { label: "25", value: 25 },
      from: 0,
      to: 0
    }
  }
}

const state = {
  ...getDefaultState()
}

const getters = {
  retainedAppointments(state) {
    return Array.from(state.retainedAppointments.values())
  },
  searchQuery(state) {
    return state.search_query
  },
  pagination(state) {
    return state.pagination
  },
  areRetainedAppointmentsLoading(state) {
    return state.areRetainedAppointmentsLoading
  }
}

const mutations = {
  SET_RETAINED_APPOINTMENTS(state, appointmentsCollection) {
    state.retainedAppointments = new Map()
    appointmentsCollection.forEach((appointmentObj) => {
      state.retainedAppointments.set(appointmentObj.id, appointmentObj)
    })
    state.retainedAppointments = new Map(state.retainedAppointments)
  },
  PUSH_RETAINED_APPOINTMENTS(state, appointmentsCollection) {
    appointmentsCollection.forEach((appointmentObj) => {
      state.retainedAppointments.set(appointmentObj.id, appointmentObj)
    })
    state.retainedAppointments = new Map(state.retainedAppointments)
  },
  SET_SEARCH_QUERY(state, payload) {
    state.search_query = payload
    state.column_search_query = ""
    state.pagination.activePage = 1
  },
  SET_COLUMN_SEARCH_QUERY(state, payload) {
    state.column_search_query = payload
    state.search_query = ""
    state.pagination.activePage = 1
  },
  SET_SORT_BY(state, payload) {
    state.sort = payload
    state.pagination.activePage = 1
  },
  SET_BETWEEN_DATES(state, payload) {
    state.betweenDates = payload
  },
  SET_RETAIN_TYPE(state, payload) {
    state.retain_type = payload
  },
  SET_GRADE_YEARS(state, payload) {
    state.grade_years = payload
  },
  SET_ACTIVE_PAGE(state, payload) {
    state.pagination.activePage = payload
  },
  SET_PER_PAGE(state, payload) {
    state.pagination.activePage = 1
    state.pagination.per_page = payload
  },
  UPDATE_LAZY_LOADING_PAGINATION(state) {
    if (state.retainedAppointments.size > 0) {
      state.pagination.from = 1
      state.pagination.to = state.retainedAppointments.size
    }
  },
  SET_PAGINATION(state, payload) {
    state.pagination = payload
  },
  SET_ARE_RETAINED_APPOINTMENTS_LOADING(state, value) {
    state.areRetainedAppointmentsLoading = value
  },
  RESET_STORE(state) {
    Object.assign(state, getDefaultState())
  }
}

const actions = {
  async fetchRetainedAppointments(
    { commit, state },
    { isLazyLoading = false } = {}
  ) {
    commit("SET_ARE_RETAINED_APPOINTMENTS_LOADING", true)

    let params = {
      page: state.pagination.activePage,
      per_page: state.pagination.per_page.value,
      search_query: state.column_search_query || state.search_query,
      sort: state.sort,
      between_dates: state.betweenDates,
      pass_type: state?.retain_type?.value ? [state.retain_type.value] : null
    }

    params = Object.entries(params).reduce(
      (a, [k, v]) => (v == null ? a : ((a[k] = v), a)),
      {}
    )

    try {
      const response = await apiV7nocache.get(
        retainExtraPassesConstants.RETAINED_APPOINTMENTS_ENDPOINT,
        {
          params: params
        }
      )

      let data = response.data.data
      const meta = response.data.meta

      if (data) {
        if (isLazyLoading) {
          commit("PUSH_RETAINED_APPOINTMENTS", data)
        } else {
          commit("SET_RETAINED_APPOINTMENTS", data)
        }

        if (meta) {
          if (isLazyLoading) {
            const currentLoadedCount = state.retainedAppointments.size
            commit("SET_PAGINATION", {
              total: meta.total,
              activePage: meta.current_page,
              pages: meta.last_page,
              per_page: { label: `${meta.per_page}`, value: meta.per_page },
              from: 1,
              to: currentLoadedCount
            })
          } else {
            commit("SET_PAGINATION", {
              total: meta.total,
              activePage: meta.current_page,
              pages: meta.last_page,
              per_page: { label: `${meta.per_page}`, value: meta.per_page },
              from: meta.from,
              to: meta.to
            })
          }
        }
      }

      return response
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error fetching retained appointments:", error)
      throw error
    } finally {
      commit("SET_ARE_RETAINED_APPOINTMENTS_LOADING", false)
    }
  },

  async exportRetainedAppointments({ state }, reqParams = {}) {
    const params = {
      page: state.pagination.activePage,
      per_page: [25, 50, 100].includes(state.pagination.per_page.value)
        ? state.pagination.per_page.value
        : "all",
      between_dates: state.betweenDates,
      ...reqParams
    }

    if (state?.column_search_query || state?.search_query) {
      params.search_query = state.column_search_query || state.search_query
    }

    if (state?.sort) {
      params.sort = state.sort
    }

    if (state?.retain_type?.value) {
      params.pass_type = [state.retain_type.value]
    }

    try {
      const response = await apiV7nocache.post(
        retainExtraPassesConstants.RETAINED_APPOINTMENTS_ENDPOINT + "/export",
        params
      )
      return response
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error exporting retained appointments:", error)
      throw error
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
